import { lazy, Suspense, useEffect, useState } from 'react';
import { Pestanas } from '../components/Pestanas';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell, LabelList,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { TopBar } from '../components/TopBar';
import { ChartCard } from '../components/ChartCard';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { useLive } from '../lib/useLive';
import { PeriodPicker, PeriodNote, ponerPeriodo, periodoTexto } from '../components/PeriodPicker';
import { Ventana } from '../components/Ventana';
import { CitasSinPaciente } from '../components/CitasSinPaciente';
import { Dona } from '../components/Dona';
import { Barras } from '../components/Barras';
import { ListaDetalle, PedidoLista } from '../components/ListaDetalle';
import { textoDeError } from '../components/Dialogo';
import { CATEGORICO, RAMPA, RESULTADO, EJE, dinero as money, dineroEje, entero, porcentaje, enPeriodo } from '../lib/graficas';
import { TabPerfiles, PersonaVentana, Demografia } from './AnaliticaPerfiles';
import { TabVender, TabEmbudo, InsightsResp, EmbudoResp } from './AnaliticaVender';
import { usePeriodoInicial } from '../lib/preferencias';
// El mapa (Leaflet + sus estilos) sólo se descarga al abrir su pestaña.
const MapaPacientes = lazy(() => import('../components/MapaPacientes').then((m) => ({ default: m.MapaPacientes })));

const GOLD = '#AC996C', GOLD_DARK = '#8b7355', GOLD_LIGHT = '#cbb88e';
/** Rejilla de las gráficas: líneas finas, sólidas y apenas visibles (punteada se lee como "proyección"). */
const GRID = { stroke: '#efece6', vertical: false } as const;

interface Fuente { fuente: string; etiqueta: string; tickets: number; ingreso: number; desde: string | null; hasta: string | null; ultimoRegistro: string | null }
interface Resp {
  kpis: { ingreso: number; tickets: number; lineas: number; ticketPromedio: number; ltvPromedio: number; pacientesActivos: number; pacientesQueCompraron: number; sinPaciente: { tickets: number; monto: number } };
  nota: string;
  fuentes: Fuente[];
  funnel: { etapa: string; n: number; pct: number }[];
  revenueByTreatment: { nombre: string; n: number; ingreso: number }[];
  treatmentMix: { nombre: string; n: number; ingreso?: number; otros?: number }[];
  revenueByAsesora: { asesora: string; ingreso: number; tickets?: number }[];
  monthly: { mes: string; asistidas: number; ingreso: number; tickets: number }[];
  forecast: { mes: string; asistidas: number; ingreso: number }[];
  personas: { persona: string; n: number; pct?: number }[];
  demografia?: Demografia;
  ageRanges: { rango: string; n: number }[];
  channelConversion: { canal: string; leads: number; pacientes: number; conv: number }[];
  ltvBuckets: { bucket: string; n: number }[];
  recompraOpportunity: { pacientes: number; valorEstimado: number };
  fallaron?: string[];
  periodo?: { label: string; all: boolean; aviso: string | null };
}

/** Una familia de "Qué piden" (interes.js) con los textos tal como llegaron. */
interface FamiliaPedida { clave: string; nombre: string; n: number; canceladas: number; pct: number; ejemplos: string[]; distintos: number; textos: { texto: string; n: number; canceladas: number; ia?: boolean; estado?: 'vacio' | 'pendiente' | 'ia_no' | 'sin_ia' }[] }
interface SinEspecificar { vacias: number; porClasificar: number; iaNo: number; sinIA: number; motivo: string | null }

/** Flujo VIVO de citas (web/call center + bot Halory) — excluye histórico CRM. */
interface CitasResp {
  nota: string;
  kpis: { total: number; nuevos: number; recompra: number; canceladas: number; cancelPct: number; completadas: number; reagendadas: number; ticketEstimado: number; ticketCitas?: number; pacientesConCuestionario: number; pacientesDistintos: number };
  porMes: { mes: string; citas: number; canceladas: number }[];
  porVia: { via: string; n: number }[];
  canales: { canal: string; n: number; canceladas: number }[];
  asesoras: { asesora: string; n: number; canceladas: number }[];
  tratamientos: FamiliaPedida[];
  menciones?: FamiliaPedida[] | null;
  sinEspecificar?: SinEspecificar;
  tipoCita: { tipo: string; n: number }[];
  nuevoVsRecompra: { tipo: string; n: number; canceladas: number }[];
  sexo: { sexo: string; n: number }[];
  edades: { rango: string; n: number }[];
  intereses: { interes: string; n: number }[];
  cancelAnticipo: { rango: string; n: number }[];
  proximas: { fecha: string; n: number }[];
}

/**
 * ANALÍTICA (0.46.0: revisada gráfica por gráfica). Cada gráfica tiene por título la PREGUNTA que contesta, una línea de
 * cómo leerla, el vacío con su razón, y casi todas se abren (tocar una barra → quiénes son). Lo que más sirve para decidir
 * va primero: "Para vender" (conclusiones con su muestra) y el Embudo (por canal y por asesoría).
 * La clave de cada pestaña no cambió (`?tab=embudo|ingresos|perfiles|mapa|pred|citas`); `vender` es nueva.
 */
const TABS: [string, string][] = [['vender', 'Para vender'], ['embudo', 'Embudo'], ['ingresos', 'Ingresos'], ['citas', 'Citas'], ['perfiles', 'Perfiles'], ['mapa', 'Mapa'], ['pred', 'Pronóstico']];

export function Analitica() {
  const { user } = useAuth();
  const [data, setData] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);
  // La pestaña se puede abrir por liga (?tab=mapa).
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get('tab'); return t && TABS.some(([k]) => k === t) ? t : 'vender'; });
  const [asesoras, setAsesoras] = useState<{ canonical_name: string }[]>([]);
  const [filtro, setFiltro] = useState('');
  const [periodo, setPeriodo] = useState(usePeriodoInicial());
  const [persona, setPersona] = useState<string | null>(null);
  // Perfiles escondidos en la dona (se comparte entre la tarjeta y "Ampliar").
  const [perfilesOcultos, setPerfilesOcultos] = useState<Set<string>>(new Set());
  // Qué piden: la familia abierta y si se cuenta lo principal o todo lo que se menciona.
  const [pedidoAbierto, setPedidoAbierto] = useState<FamiliaPedida | null>(null);
  const [modoPedidos, setModoPedidos] = useState<'principal' | 'menciones'>('principal');
  // Lo que hay detrás de una barra (citas, personas o ventas).
  const [lista, setLista] = useState<PedidoLista | null>(null);

  // `silent` distingue las dos recargas: cuando el usuario cambia de asesora se
  // vacía la vista (está esperando otro corte y el spinner es correcto); cuando
  // el aviso viene de otra persona NO se vacía — las gráficas se actualizan sin
  // parpadear debajo de quien las está leyendo.
  const load = (a: string, per = periodo, silent = false) => {
    if (!silent) setData(null);
    const qs = new URLSearchParams();
    if (a) qs.set('asesora', a);
    ponerPeriodo(qs, per);
    const s = qs.toString();
    return api<Resp>('analytics' + (s ? `?${s}` : '')).then(setData).catch((e) => setError(e.message));
  };
  useEffect(() => { load(''); api<{ asesoras: any[] }>('crm/asesoras').then((r) => setAsesoras(r.asesoras.filter((x) => x.activa))).catch(() => {}); }, []);
  useLive(['followup', 'patient', 'config'], () => load(filtro, periodo, true));

  // Citas en vivo: se consulta al abrir su pestaña (y al cambiar de asesora), directo de la DB.
  const [citas, setCitas] = useState<CitasResp | null>(null);
  const [citasErr, setCitasErr] = useState<string | null>(null);
  const cargarCitas = (silencioso = false) => {
    if (!silencioso) { setCitas(null); setCitasErr(null); }
    // El periodo de arriba también manda aquí (desde 0.39.0): citas AGENDADAS en esas fechas.
    const qs = ponerPeriodo(new URLSearchParams(filtro ? { asesora: filtro } : {}), periodo).toString();
    api<CitasResp>('analytics/citas' + (qs ? `?${qs}` : ''))
      .then(setCitas).catch((e) => { if (!silencioso) setCitasErr(e.message); });
  };
  useEffect(() => { if (tab === 'citas') cargarCitas(); }, [tab, filtro, periodo]);
  // Para vender: todo el historial (no depende del periodo), sí de la asesoría.
  const [ins, setIns] = useState<InsightsResp | null>(null);
  const [insErr, setInsErr] = useState<string | null>(null);
  useEffect(() => {
    if (tab !== 'vender') return;
    setIns(null); setInsErr(null);
    api<InsightsResp>('analytics/insights' + (filtro ? `?asesora=${encodeURIComponent(filtro)}` : '')).then(setIns).catch((e) => setInsErr(textoDeError(e)));
  }, [tab, filtro]);
  // Embudo: de quienes llegaron en el periodo.
  const [emb, setEmb] = useState<EmbudoResp | null>(null);
  const [embErr, setEmbErr] = useState<string | null>(null);
  useEffect(() => {
    if (tab !== 'embudo') return;
    setEmb(null); setEmbErr(null);
    const qs = ponerPeriodo(new URLSearchParams(filtro ? { asesora: filtro } : {}), periodo).toString();
    api<EmbudoResp>('analytics/embudo' + (qs ? `?${qs}` : '')).then(setEmb).catch((e) => setEmbErr(textoDeError(e)));
  }, [tab, filtro, periodo]);
  /** El periodo y la asesoría de la pantalla, para que el detalle cuente lo mismo que la gráfica. */
  const conFiltros = (qs: Record<string, string>) => {
    const u = ponerPeriodo(new URLSearchParams(qs), periodo);
    if (filtro) u.set('asesora', filtro);
    return Object.fromEntries(u.entries());
  };
  const periodoTxt = periodoTexto(periodo);
  const enPer = enPeriodo(periodo);
  const cargando = (txt: string) => <div className="muted" style={{ padding: 40, textAlign: 'center' }}>{txt}</div>;
  const errorBox = (e: string) => <div className="card" style={{ padding: 16, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{e}</div>;

  return (
    <div>
      <TopBar />
      <style>{`
        .an-filtros { display: flex; gap: 10px; align-items: flex-start; flex-wrap: wrap; margin: 0 2px 14px; }
        .an-filtros > .pp { flex: 1 1 420px; min-width: 0; }
        .an-filtros select { width: auto; max-width: 100%; padding: 7px 10px; border: 1px solid var(--border); border-radius: 10px; background: #fff; font-family: inherit; font-size: 14px; }
        .an-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin: 8px 0 16px; }
        @media (max-width: 480px) { .an-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        .an-dos { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr)); gap: 14px; align-items: start; }
      `}</style>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '18px 16px 48px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, margin: '4px 2px 6px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Analítica</h1>
          {(filtro || user?.scope === 'all' || user?.tenantName) && <span className="badge">{filtro || (user?.scope === 'all' ? 'Todas las clínicas' : user?.tenantName)}</span>}
        </div>

        {/* Los filtros en UNA barra: periodo y asesoría, para todo lo de abajo. */}
        <div className="an-filtros" data-filtros>
          <PeriodPicker value={periodo} onChange={(v) => { setPeriodo(v); load(filtro, v); }} />
          <select value={filtro} onChange={(e) => { setFiltro(e.target.value); load(e.target.value, periodo); }} aria-label="Asesoría" data-filtro-asesoria>
            <option value="">Todo el equipo</option>
            {asesoras.map((a) => <option key={a.canonical_name} value={a.canonical_name}>{a.canonical_name}</option>)}
          </select>
        </div>
        <PeriodNote>{data?.periodo?.aviso}</PeriodNote>
        {data?.fallaron?.length ? (
          <div className="card" style={{ padding: '9px 12px', marginBottom: 12, background: 'var(--warning-bg)', color: 'var(--warning)', fontSize: 13, border: 'none' }}>
            No se pudieron calcular {data.fallaron.length} panel(es): {data.fallaron.join(' · ')}
          </div>
        ) : null}

        {error && errorBox(error)}
        {!data && !error && cargando('Calculando analítica…')}
        {persona && <PersonaVentana persona={persona} periodo={periodo} onClose={() => setPersona(null)} />}
        {pedidoAbierto && <PedidoDetalle f={pedidoAbierto} total={citas?.kpis.total || 0} sin={citas?.sinEspecificar} onClose={() => setPedidoAbierto(null)} />}
        {lista && <ListaDetalle pedido={lista} onClose={() => setLista(null)} />}

        {data && (
          <div className="fade-in">
            <div className="an-kpis" data-kpis>
              <Kpi label="Ingreso cobrado" value={money(data.kpis.ingreso)} accent hint={periodoTxt} />
              <Kpi label="Ticket promedio" value={money(data.kpis.ticketPromedio)} hint={data.kpis.tickets ? `${entero(data.kpis.tickets)} tickets` : 'sin tickets'} />
              <Kpi label="LTV promedio" value={money(data.kpis.ltvPromedio)} hint={data.kpis.pacientesQueCompraron ? `${entero(data.kpis.pacientesQueCompraron)} pacientes que compraron` : 'nadie compró en el periodo'} />
              <Kpi label="Pacientes activos" value={entero(data.kpis.pacientesActivos)} hint="vinieron alguna vez (hoy)" />
              <Kpi label="Oportunidad de recompra" value={money(data.recompraOpportunity.valorEstimado)} accent hint={`${entero(data.recompraOpportunity.pacientes)} ya les toca volver · estimado`} />
            </div>
            <Fuentes fuentes={data.fuentes} nota={data.nota} />

            <Pestanas items={TABS} valor={tab} onCambio={setTab} etiqueta="Vistas de Analítica" estilo={{ marginBottom: 16 }} />

            {tab === 'vender' && (insErr ? errorBox(insErr) : !ins ? cargando('Sacando conclusiones…') : <TabVender d={ins} onLista={setLista} />)}

            {tab === 'embudo' && (embErr ? errorBox(embErr) : !emb ? cargando('Calculando el embudo…') : <TabEmbudo e={emb} periodoTxt={enPer} asesoria={filtro} onLista={(p) => setLista({ ...p, qs: conFiltros(p.qs) })} />)}

            {tab === 'ingresos' && <TabIngresos data={data} periodoTxt={enPer} onLista={(p, conPeriodo = true) => setLista({ ...p, qs: conPeriodo ? conFiltros(p.qs) : { ...p.qs, ...(filtro ? { asesora: filtro } : {}) } })} />}

            {tab === 'perfiles' && (
              <TabPerfiles personas={data.personas} demografia={data.demografia} ocultos={perfilesOcultos} setOcultos={setPerfilesOcultos} periodo={periodo} onAbrir={setPersona} />
            )}

            {tab === 'mapa' && (
              <Suspense fallback={cargando('Cargando el mapa…')}>
                <MapaPacientes />
              </Suspense>
            )}

            {tab === 'pred' && <TabPred data={data} />}

            {tab === 'citas' && (
              <div style={{ display: 'grid', gap: 14, minWidth: 0 }}>
                {/* Las citas de Halory que no se pudieron ligar a un expediente (0.49.0): aquí, donde se miran las citas. */}
                {user?.role === 'admin' && <CitasSinPaciente onCambio={() => cargarCitas(true)} />}
                {citasErr && errorBox(citasErr)}
                {!citas && !citasErr && cargando('Consultando citas en vivo…')}
                {citas && (
                  <TabCitas c={citas} modoPedidos={modoPedidos} setModoPedidos={setModoPedidos} onPedido={setPedidoAbierto} />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, hint }: { label: string; value: string; accent?: boolean; hint?: string }) {
  return (
    <div className="card" style={{ padding: '13px 15px', minWidth: 0 }}>
      <div className="muted" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 'clamp(18px, 5vw, 22px)', fontWeight: 700, marginTop: 4, color: accent ? 'var(--gold-dark)' : 'var(--ink)', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={value}>{value}</div>
      {hint && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

/** De dónde sale el dinero de esta pantalla y hasta cuándo llega cada fuente (una línea; el detalle en el `title`).
 *  Un mes en cero se ve igual cuando no se vendió que cuando falta subir el corte, y esto es lo que los distingue. */
function Fuentes({ fuentes, nota }: { fuentes: Fuente[]; nota: string }) {
  const fecha = (d: string | null) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
  const hay = fuentes.some((f) => f.tickets > 0);
  return (
    <div className="muted" data-fuentes style={{ padding: '0 2px', marginBottom: 12, display: 'flex', gap: '4px 14px', flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5 }}>
      <strong style={{ color: 'var(--gold-dark)' }}>Dinero real:</strong>
      {hay ? fuentes.filter((f) => f.tickets > 0).map((f) => (
        <span key={f.fuente}>
          <strong style={{ color: 'var(--ink)' }}>{f.etiqueta}</strong> {entero(f.tickets)} tickets · {money(f.ingreso)} · {f.desde === f.hasta ? fecha(f.desde) : `${fecha(f.desde)} → ${fecha(f.hasta)}`}
        </span>
      )) : <span>{nota}</span>}
      {hay && <span style={{ fontSize: 11.5 }}>Los canjes de puntos van a $0 y no cuentan.</span>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Ingresos
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
function TabIngresos({ data, periodoTxt, onLista }: { data: Resp; periodoTxt: string; onLista: (p: PedidoLista, conPeriodo?: boolean) => void }) {
  const meses = data.monthly.filter((m) => m.ingreso > 0);
  const ltv = data.ltvBuckets;
  const LTV_CLAVE = ['l1', 'l2', 'l3', 'l4', 'l5'];
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <ChartCard title="¿Cuánto se cobra cada mes?" datos="ingreso-mes" data={meses} hint="todo el historial"
        como="Lo que de verdad se cobró cada mes (recepción y la tienda de la app). Toca un mes para ver sus ventas."
        vacio="Todavía no hay ventas cobradas: se ven en cuanto recepción cobre."
        foot={<>No se recorta por el periodo de arriba: es el contexto contra el que se lee.</>}
        render={(h) => (
          <ResponsiveContainer width="100%" height={h}>
            <BarChart data={meses} margin={{ left: 0, right: 8, top: 6 }}>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#6b6b6b' }} axisLine={false} tickLine={false} interval={h > 300 ? 0 : 'preserveStartEnd'} />
              <YAxis tickFormatter={(v) => dineroEje(Number(v))} tick={EJE} axisLine={false} tickLine={false} width={48} />
              <Tooltip cursor={{ fill: 'rgba(172,153,108,0.08)' }} formatter={(v: any, _n: any, p: any) => [`${money(Number(v))} · ${entero(p?.payload?.tickets || 0)} tickets`, 'Cobrado']} />
              <Bar dataKey="ingreso" name="Cobrado" fill={GOLD} radius={[4, 4, 0, 0]} maxBarSize={36} style={{ cursor: 'pointer' }} isAnimationActive={false}
                onClick={(d: any) => { const m = d?.payload?.mes || d?.mes; if (m) onLista({ titulo: `Ventas de ${m}`, qs: { tipo: 'ventas', mes: m } }, false); }} />
            </BarChart>
          </ResponsiveContainer>
        )} />
      <div className="an-dos">
        <ChartCard title="¿Qué deja más dinero?" datos="ingreso-tratamiento" data={data.revenueByTreatment}
          como={`Lo cobrado por cada cosa ${periodoTxt}. Toca una para ver sus ventas.`}
          vacio="No hay ventas en este periodo."
          foot={<>Las ventas de recepción y de la tienda se nombran por el catálogo; un concepto libre, como se capturó.</>}
          render={() => (
            <Barras filas={data.revenueByTreatment.map((t) => ({ clave: t.nombre, nombre: t.nombre, n: t.ingreso, extra: `· ${entero(t.n)} ${t.n === 1 ? 'venta' : 'ventas'}` }))}
              total={Math.max(data.kpis.ingreso, data.revenueByTreatment.reduce((a, t) => a + t.ingreso, 0))}
              formato={(f, tot) => `${money(f.n)} · ${porcentaje(f.n, tot)}`} datos="ingreso-tratamiento"
              onAbrir={(k) => onLista({ titulo: k, subtitulo: `Lo cobrado ${periodoTxt}.`, qs: { tipo: 'ventas', concepto: k } })} />
          )} />
        <ChartCard title="¿Qué se vende más?" datos="mix" data={data.treatmentMix}
          como="Cuántas veces se cobró cada cosa (línea por línea) y su % del total. Las seis primeras; el resto va en «Otros»."
          vacio="No hay ventas en este periodo."
          render={(h) => (
            <Dona datos={data.treatmentMix.map((t) => ({ clave: t.nombre, nombre: t.nombre, n: t.n, titulo: t.otros ? `${t.otros} cosas distintas` : undefined }))} alto={Math.min(h, 260)} unidad="cobros" datosTest="mix"
              onAbrir={(k) => { if (k !== 'Otros') onLista({ titulo: k, subtitulo: `Lo cobrado ${periodoTxt}.`, qs: { tipo: 'ventas', concepto: k } }); }} />
          )} />
      </div>
      <div className="an-dos">
        <ChartCard title="¿Cuánto ha dejado cada paciente?" datos="ltv" data={ltv.some((b) => b.n > 0) ? ltv : []}
          como={`Lo cobrado a cada paciente ${periodoTxt} (su LTV), por rangos. Toca un rango para ver quiénes son.`}
          vacio="Nadie con expediente compró en este periodo."
          render={() => (
            <Barras filas={ltv.map((b, i) => ({ clave: LTV_CLAVE[i], nombre: b.bucket, n: b.n, color: RAMPA[i + 1] }))} datos="ltv"
              onAbrir={(k) => { const b = ltv[LTV_CLAVE.indexOf(k)]; onLista({ titulo: `Pacientes que han dejado ${b.bucket}`, qs: { tipo: 'ventas', ltv: k } }); }} />
          )} />
        <ChartCard title="¿Cuánto compra la cartera de cada asesoría?" datos="ingreso-asesoria" data={data.revenueByAsesora}
          como="La venta no dice quién la vendió: cuenta para quien tiene asignado el expediente."
          vacio="No hay ventas en este periodo."
          foot={<>«Sin paciente» es dinero que no se empató con un expediente; «Sin asignar», expedientes sin nadie de ventas asignado.</>}
          render={() => (
            <Barras filas={data.revenueByAsesora.map((a) => ({ clave: a.asesora, nombre: a.asesora, n: a.ingreso, extra: a.tickets ? `· ${entero(a.tickets)} ${a.tickets === 1 ? 'ticket' : 'tickets'}` : undefined }))}
              formato={(f, tot) => `${money(f.n)} · ${porcentaje(f.n, tot)}`} datos="ingreso-asesoria" />
          )} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Citas (flujo vivo)
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
function TabCitas({ c, modoPedidos, setModoPedidos, onPedido }: {
  c: CitasResp; modoPedidos: 'principal' | 'menciones'; setModoPedidos: (m: 'principal' | 'menciones') => void; onPedido: (f: FamiliaPedida) => void;
}) {
  const nuevos = c.nuevoVsRecompra.map((t) => ({ clave: t.tipo, nombre: t.tipo === 'Nuevo' ? 'Primera vez' : 'Ya había venido', n: t.n, extra: t.canceladas ? `· ${t.canceladas} canceladas` : undefined }));
  return (
    <>
      <div className="an-kpis" style={{ margin: 0 }}>
        <Kpi label="Citas que entraron" value={entero(c.kpis.total)} accent hint="sitio, call center y la IA de Halory" />
        <Kpi label="Llegaron" value={entero(c.kpis.completadas)} hint={c.kpis.total ? `${porcentaje(c.kpis.completadas, c.kpis.total)} de las que entraron` : undefined} />
        <Kpi label="Canceladas" value={`${entero(c.kpis.canceladas)} (${c.kpis.cancelPct} %)`} />
        <Kpi label="Reagendadas" value={entero(c.kpis.reagendadas)} />
        <Kpi label="Ticket estimado" value={money(c.kpis.ticketEstimado)} hint={c.kpis.ticketCitas ? `precio de lista de lo que piden · ${c.kpis.ticketCitas} citas` : 'nadie pidió algo con precio de lista'} />
      </div>
      <div className="an-dos">
        <ChartCard title="¿Cuántas citas entran cada mes?" datos="citas-mes" data={c.porMes}
          como="Por la fecha en que se agendaron. Dorado: todas; naranja: las que se cancelaron."
          vacio="No entraron citas por el sitio ni por Halory en este periodo."
          render={(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={c.porMes} margin={{ left: -14, top: 6 }} barGap={2}>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="mes" tick={EJE} axisLine={false} tickLine={false} />
                <YAxis tick={EJE} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: 'rgba(172,153,108,0.08)' }} /><Legend iconType="square" wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span style={{ color: 'var(--ink)' }}>{v}</span>} />
                <Bar dataKey="citas" name="Citas" fill={GOLD} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                <Bar dataKey="canceladas" name="Canceladas" fill={RESULTADO.cancelo} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          )} />
        <ChartCard title="¿Qué piden?" datos="que-piden" data={c.tratamientos}
          como="Lo que pidió cada cita, agrupado por tratamiento aunque lo escriban distinto. Toca una barra para ver cómo lo pidieron."
          vacio="No entraron citas en este periodo."
          foot={<>
            <strong>Valoración / consulta</strong> = pidió una consulta sin decir de qué tratamiento. <strong>Sin especificar</strong> = el texto no dice nada reconocible (o viene vacío).
            {c.menciones ? <> «Todo lo que mencionan» cuenta una cita en cada tratamiento que nombra («botox y labios» suma en los dos).</> : null}
            {' '}Lo que las reglas no saben leer («quiere tratar la flacidez») lo clasifica la IA una sola vez; al abrir la barra se ve cuáles.
            {c.sinEspecificar?.motivo ? <span data-sin-especificar-motivo><br /><strong>Sin especificar:</strong> {c.sinEspecificar.motivo}.</span> : null}
          </>}
          render={(h) => (
            <QuePiden lista={modoPedidos === 'menciones' && c.menciones ? c.menciones : c.tratamientos} h={h}
              modo={c.menciones ? modoPedidos : null} onModo={setModoPedidos} onAbrir={onPedido} />
          )} />
        <ChartCard title="¿Por dónde llegan las citas?" datos="citas-canal" data={c.canales}
          como="El canal que se anotó en cada cita, y cuántas de ésas se cancelaron."
          vacio="No entraron citas en este periodo."
          render={() => <Barras filas={c.canales.map((x) => ({ clave: x.canal, nombre: x.canal, n: x.n, extra: x.canceladas ? `· ${x.canceladas} canceladas` : undefined }))} datos="citas-canal" />} />
        <ChartCard title="¿Quién toma las citas?" datos="citas-via" data={c.porVia}
          como="Las que agenda la IA de Halory contra las que entran por el sitio o el call center."
          vacio="No entraron citas en este periodo."
          render={(h) => <Dona datos={c.porVia.map((v) => ({ clave: v.via, nombre: v.via, n: v.n }))} alto={Math.min(h, 230)} unidad="citas" datosTest="via" />} />
        <ChartCard title="¿Son nuevos o ya habían venido?" datos="citas-nuevos" data={nuevos}
          como="Si antes de esta cita la persona ya tenía otra cita o un tratamiento en el expediente."
          vacio="No entraron citas en este periodo."
          render={() => <Barras filas={nuevos} colores={{ Nuevo: CATEGORICO[0], Recompra: CATEGORICO[2] }} datos="citas-nuevos" />} />
        <ChartCard title="¿Quién las agendó?" datos="citas-asesoria" data={c.asesoras}
          como="La asesoría que se anotó en la cita, y cuántas de ésas se cancelaron."
          vacio="No entraron citas en este periodo."
          render={() => <Barras filas={c.asesoras.map((x) => ({ clave: x.asesora, nombre: x.asesora, n: x.n, extra: x.canceladas ? `· ${x.canceladas} canceladas` : undefined }))} datos="citas-asesoria" />} />
        <ChartCard title="¿Con cuánta anticipación cancelan?" datos="citas-cancelan" data={c.cancelAnticipo}
          como="Cuánto antes de la cita se registró la cancelación. Lo de «el mismo día o después» casi siempre es alguien que no llegó."
          vacio="No hubo cancelaciones en este periodo."
          render={() => <Barras filas={c.cancelAnticipo.map((x) => ({ clave: x.rango, nombre: x.rango, n: x.n, color: RESULTADO.cancelo }))} datos="citas-cancelan" />} />
        <ChartCard title="¿Qué viene en las próximas dos semanas?" datos="citas-proximas" data={c.proximas} hint="no depende del periodo"
          como="Citas programadas o confirmadas por día, de hoy a 14 días."
          vacio="No hay citas programadas en las próximas dos semanas."
          render={(h) => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={c.proximas.map((x) => ({ ...x, dia: new Date(x.fecha + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' }) }))} margin={{ left: -20, top: 16 }}>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="dia" tick={{ fontSize: 10, fill: '#6b6b6b' }} axisLine={false} tickLine={false} />
                <YAxis tick={EJE} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: 'rgba(172,153,108,0.08)' }} /><Bar dataKey="n" name="Citas" fill={GOLD_DARK} radius={[4, 4, 0, 0]} maxBarSize={30} isAnimationActive={false}>
                  <LabelList dataKey="n" position="top" style={{ fontSize: 10.5, fill: '#6b6b6b' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )} />
        <ChartCard title="¿Qué quieren mejorar?" datos="citas-intereses" data={c.intereses} hint={`${c.kpis.pacientesConCuestionario} de ${c.kpis.pacientesDistintos} con cuestionario`}
          como="Lo que marcaron en el cuestionario de DraIA quienes agendaron (una persona puede marcar varias cosas)."
          vacio="Nadie de quienes agendaron ha llenado el cuestionario de DraIA."
          render={() => <Barras filas={c.intereses.map((x) => ({ clave: x.interes, nombre: x.interes, n: x.n }))} total={c.kpis.pacientesConCuestionario || undefined}
            formato={(f, tot) => `${entero(f.n)} · ${porcentaje(f.n, tot)}`} datos="citas-intereses" />} />
      </div>
      <p className="muted" style={{ fontSize: 12 }}>{c.nota}</p>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Pronóstico
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
function TabPred({ data }: { data: Resp }) {
  const serie = data.monthly.filter((m) => m.ingreso > 0);
  const lineas = (real: { mes: string; v: number }[], pred: { mes: string; v: number }[]) => {
    const t = [...real.map((m) => ({ mes: m.mes, real: m.v as number | null, pred: null as number | null })), ...pred.map((f) => ({ mes: f.mes, real: null as number | null, pred: f.v as number | null }))];
    if (real.length && pred.length) t[real.length - 1].pred = real[real.length - 1].v;
    return t;
  };
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div className="card" style={{ padding: 18, background: 'linear-gradient(135deg, rgba(172,153,108,0.14), rgba(172,153,108,0.04))' }} data-oportunidad>
        <div className="muted" style={{ fontSize: 13 }}>¿Cuánto dinero está esperando que alguien le escriba? Pacientes a los que ya les toca volver (pasaron su ventana de retoque), por el ticket promedio real.</div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'baseline', marginTop: 6, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--gold-dark)' }}>{money(data.recompraOpportunity.valorEstimado)}</div>
          <div className="muted">{entero(data.recompraOpportunity.pacientes)} pacientes · estimado · para escribirles desde Halory</div>
        </div>
      </div>
      <ChartCard title="¿Cuántas citas se cumplirán los próximos meses?" datos="pred-citas" data={data.monthly}
        como="Línea oscura: citas que se cumplieron cada mes. Punteada: lo que sigue si la tendencia no cambia (regresión lineal)."
        vacio="Todavía no hay meses con citas cumplidas."
        render={(h) => (
          <ResponsiveContainer width="100%" height={h}>
            <LineChart data={lineas(data.monthly.map((m) => ({ mes: m.mes, v: m.asistidas })), data.forecast.map((f) => ({ mes: f.mes, v: f.asistidas })))} margin={{ left: -12, right: 12, top: 6 }}>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#6b6b6b' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
              <YAxis tick={EJE} axisLine={false} tickLine={false} />
              <Tooltip /><Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span style={{ color: 'var(--ink)' }}>{v}</span>} />
              <Line type="monotone" dataKey="real" name="Se cumplieron" stroke={GOLD_DARK} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
              <Line type="monotone" dataKey="pred" name="Pronóstico" stroke={GOLD} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        )} />
      <ChartCard title="¿Cuánto se cobrará los próximos meses?" datos="pred-ingreso" data={serie}
        como="Línea oscura: lo cobrado cada mes. Punteada: lo que sigue si la tendencia no cambia."
        vacio="Todavía no hay meses con ventas cobradas."
        render={(h) => (
          <ResponsiveContainer width="100%" height={h}>
            <LineChart data={lineas(serie.map((m) => ({ mes: m.mes, v: m.ingreso })), data.forecast.map((f) => ({ mes: f.mes, v: f.ingreso })))} margin={{ left: 0, right: 12, top: 6 }}>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#6b6b6b' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => dineroEje(Number(v))} tick={EJE} axisLine={false} tickLine={false} width={48} />
              <Tooltip formatter={(v: any) => money(Number(v))} />
              <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span style={{ color: 'var(--ink)' }}>{v}</span>} />
              <Line type="monotone" dataKey="real" name="Cobrado" stroke={GOLD_DARK} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
              <Line type="monotone" dataKey="pred" name="Pronóstico" stroke={GOLD} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        )} />
      <p className="muted" style={{ fontSize: 12 }}>
        Los pronósticos son una regresión lineal simple: dicen hacia dónde va la tendencia, no lo que va a pasar. Con pocos meses de datos (o un mes atípico) se mueven mucho.
      </p>
    </div>
  );
}

/** "Qué piden" por familia de tratamiento (0.44.0). Cada barra abre los textos tal como llegaron. */
function QuePiden({ lista, h, modo, onModo, onAbrir }: {
  lista: FamiliaPedida[]; h: number; modo: 'principal' | 'menciones' | null;
  onModo: (m: 'principal' | 'menciones') => void; onAbrir: (f: FamiliaPedida) => void;
}) {
  const filas = lista.map((f) => ({ ...f, corto: f.nombre.length > 24 ? f.nombre.slice(0, 23) + '…' : f.nombre, etq: `${f.n.toLocaleString('es-MX')} · ${f.pct} %` }));
  const alto = Math.max(h, filas.length * 26 + 24);
  return (
    <div data-que-piden>
      {modo && (
        <div role="group" aria-label="Cómo contar" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {([['principal', 'Lo principal de cada cita'], ['menciones', 'Todo lo que mencionan']] as const).map(([k, t]) => (
            <button key={k} type="button" className="btn-ghost" aria-pressed={modo === k} data-modo-pedidos={k} onClick={() => onModo(k)}
              style={{ padding: '4px 10px', fontSize: 12, boxShadow: 'none', ...(modo === k ? { borderColor: 'var(--gold)', background: 'var(--gold-light)', color: 'var(--gold-dark)', fontWeight: 700 } : {}) }}>
              {t}
            </button>
          ))}
        </div>
      )}
      <ResponsiveContainer width="100%" height={alto}>
        <BarChart data={filas} layout="vertical" margin={{ left: 8, right: 70 }}>
          <XAxis type="number" hide />
          {/* Una sola línea por etiqueta: el <Text> de Recharts parte "Valoración / consulta" en dos y se encima con la barra de abajo. */}
          <YAxis type="category" dataKey="corto" width={150} axisLine={false} tickLine={false} interval={0}
            tick={(p: any) => <text x={p.x} y={p.y} dy={4} textAnchor="end" fontSize={11} fill="#6b6b6b" className="recharts-cartesian-axis-tick-value">{p.payload?.value}</text>} />
          {/* En celular la columna de nombres (150 px) deja la zona de barras angosta y el globo no cabía: se pega a la
              izquierda de la gráfica (encima de los nombres) en vez de salirse por la derecha y que la tarjeta lo corte. */}
          <Tooltip cursor={{ fill: 'rgba(172,153,108,0.08)' }} position={typeof window !== 'undefined' && window.innerWidth < 600 ? { x: 4 } : undefined} formatter={(v: any, _n: any, pr: any) => [`${Number(v).toLocaleString('es-MX')} citas · ${pr?.payload?.pct} %`, pr?.payload?.nombre]} labelFormatter={() => ''} />
          <Bar dataKey="n" name="Citas" radius={[0, 6, 6, 0]} barSize={14} style={{ cursor: 'pointer' }} isAnimationActive={false}
            onClick={(d: any) => { const f = d?.payload || d; if (f?.clave) onAbrir(lista.find((x) => x.clave === f.clave) || f); }}>
            {filas.map((f) => <Cell key={f.clave} fill={f.clave === 'sin_especificar' ? '#c9c3b8' : f.clave === 'valoracion' ? GOLD_LIGHT : GOLD} data-familia={f.clave} />)}
            <LabelList dataKey="etq" content={(p: any) => <text x={Number(p.x) + Number(p.width) + 6} y={Number(p.y) + Number(p.height) / 2} dy={4} fontSize={10.5} fill="#6b6b6b" data-etq-pedido>{p.value}</text>} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Los textos de una familia de "Qué piden", tal como llegaron (teléfonos y correos tapados). */
function PedidoDetalle({ f, total, sin, onClose }: { f: FamiliaPedida; total: number; sin?: SinEspecificar; onClose: () => void }) {
  const expl: Record<string, string> = {
    valoracion: 'Pidieron una consulta o valoración sin decir de qué tratamiento: lo que vengan a hacerse se sabe hasta la cita. Cuando Halory manda el tratamiento de interés con la cita, esas citas pasan a su tratamiento.',
    sin_especificar: `El texto viene vacío o no dice nada que se reconozca como tratamiento (por ejemplo, sólo «PV» o un nombre).${sin?.motivo ? ` ${sin.motivo}.` : ''}`,
  };
  const ESTADO: Record<string, string> = { vacio: 'vacío', pendiente: 'por clasificar', ia_no: 'ni la IA pudo decir qué es', sin_ia: 'IA sin configurar' };
  const porIA = f.textos.filter((t) => t.ia).reduce((a, t) => a + t.n, 0);
  return (
    <Ventana datos="pedido" ancho={720} onClose={onClose}
      titulo={<>{f.nombre} · {f.n.toLocaleString('es-MX')} {f.n === 1 ? 'cita' : 'citas'}{total ? ` (${f.pct} %)` : ''}</>}
      subtitulo={<>{expl[f.clave] ? <>{expl[f.clave]} </> : null}
        {f.distintos.toLocaleString('es-MX')} {f.distintos === 1 ? 'forma distinta' : 'formas distintas'} de pedirlo{f.canceladas ? ` · ${f.canceladas} cancelada${f.canceladas === 1 ? '' : 's'}` : ''}.
        Así llegaron (los teléfonos y correos que venían en el texto se tapan).
        {porIA ? <> {porIA} {porIA === 1 ? 'cita la clasificó' : 'citas las clasificó'} la IA porque las reglas no leían su texto.</> : null}</>}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }} data-pedido-textos>
          <thead><tr>
            <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, color: 'var(--ink-soft)' }}>Lo que pidieron</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 12, color: 'var(--ink-soft)' }}>Citas</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 12, color: 'var(--ink-soft)' }}>Canceladas</th>
          </tr></thead>
          <tbody>
            {f.textos.map((t, i) => (
              <tr key={i}>
                <td style={{ padding: '6px 8px', borderTop: '1px solid var(--border)', wordBreak: 'break-word' }}>
                  {t.texto}
                  {t.ia && <span className="badge" data-por-ia style={{ marginLeft: 6, fontSize: 10.5, background: 'var(--gold-light)', color: 'var(--gold-dark)', border: 'none' }}>clasificado por IA</span>}
                  {t.estado && <span className="muted" data-estado-texto={t.estado} style={{ marginLeft: 6, fontSize: 11 }}>· {ESTADO[t.estado]}</span>}
                </td>
                <td style={{ padding: '6px 8px', borderTop: '1px solid var(--border)', textAlign: 'right', fontWeight: 600 }}>{t.n}</td>
                <td style={{ padding: '6px 8px', borderTop: '1px solid var(--border)', textAlign: 'right' }} className="muted">{t.canceladas || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {f.distintos > f.textos.length && <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>Y {f.distintos - f.textos.length} formas más (las de menos citas).</p>}
    </Ventana>
  );
}

